// PeakPrep Motion: 3D-Bühne im Hero, ruhige Scroll-Reveals (Fallback ohne CSS-Scrolltimeline), Größenfinder, Laufband-Pause.
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/** Scrollfortschritt 0..1 eines hohen Elements, das einen sticky Inhalt trägt. */
function stickyProgress(element) {
  const rect = element.getBoundingClientRect();
  const distance = rect.height - innerHeight;
  return distance <= 0 ? 0 : Math.min(1, Math.max(0, -rect.top / distance));
}

class PPMotionStage extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.model = this.querySelector('model-viewer');
    this.photo = this.querySelector('.pp-stage__photo');
    this.lines = [...this.querySelectorAll('.pp-stage__line')];
    this.cta = this.querySelector('.pp-stage__cta');
    this.buybar = this.querySelector('.pp-buybar');
    this.turns = Number(this.dataset.turns) || 1.5;

    this.footer = document.querySelector('.page-wrapper > footer');
    const schedule = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.updateBuybar();
        if (!reducedMotion.matches) this.update();
      });
    };
    document.addEventListener('scroll', schedule, { passive: true, capture: true, signal: this.abort.signal });
    addEventListener('resize', schedule, { passive: true, signal: this.abort.signal });
    this.resizeObserver = new ResizeObserver(schedule);
    this.resizeObserver.observe(this);
    if (this.footer) this.resizeObserver.observe(this.footer);

    // „Bewegung reduzieren“ kann sich während der Sitzung ändern
    reducedMotion.addEventListener('change', () => this.applyMotionPreference(), { signal: this.abort.signal });
    this.applyMotionPreference();
    this.updateBuybar();
  }

  disconnectedCallback() {
    this.abort?.abort();
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.resizeObserver?.disconnect();
  }

  applyMotionPreference() {
    if (reducedMotion.matches || (!this.model && !this.photo)) {
      this.classList.remove('is-ready');
      this.classList.add('is-static');
      this.style.removeProperty('--p');
      if (this.model) {
        this.model.cameraOrbit = '45deg 80deg 100%';
        this.model.jumpCameraToGoal?.();
      }
      return;
    }
    this.classList.remove('is-static');
    this.classList.add('is-ready');
    this.update();
  }

  updateBuybar() {
    if (!this.buybar) return;
    const hero = this.getBoundingClientRect();
    const footerTop = this.footer?.getBoundingClientRect().top ?? Infinity;
    const shown = hero.bottom <= 0 && footerTop > innerHeight + 180;
    this.buybar.classList.toggle('is-shown', shown);
    this.buybar.inert = !shown;
  }

  update() {
    const rect = this.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > innerHeight) return;
    const compact = this.closest('.pp-dark') && matchMedia('(max-width: 989px)').matches;
    const p = compact ? Math.min(1, Math.max(0, -rect.top / rect.height)) : stickyProgress(this);
    this.style.setProperty('--p', p.toFixed(4));
    if (this.model) this.model.cameraOrbit = `${(45 + p * 360 * this.turns).toFixed(1)}deg ${(80 - p * 10).toFixed(1)}deg 100%`;
    this.lines.forEach((line, index) => line.classList.toggle('is-on', index === 0 || p > index * 0.2));
    this.cta?.classList.toggle('is-on', p > 0.62);
  }
}

class PPSizeFinder extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.select = this.querySelector('select');
    this.output = this.querySelector('[data-pp-size-output]');
    this.ranges = [...this.querySelectorAll('[data-pp-size]')].map((el) => ({
      label: el.dataset.label, min: Number(el.dataset.min), max: Number(el.dataset.max),
    }));
    this.select?.addEventListener('change', () => this.render(), { signal: this.abort.signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  render() {
    const size = Number(this.select.value);
    if (!size || !this.output) return;
    const match = this.ranges.find((range) => size >= range.min && size <= range.max);
    this.output.replaceChildren();
    if (match) {
      const badge = document.createElement('strong');
      badge.textContent = match.label;
      this.output.append(badge, ` ${this.dataset.matchText}`);
    } else {
      this.output.textContent = this.dataset.noMatchText;
    }
    this.output.classList.remove('is-pop');
    void this.output.offsetWidth;
    this.output.classList.add('is-pop');
  }
}

if (!customElements.get('pp-motion-stage')) customElements.define('pp-motion-stage', PPMotionStage);
if (!customElements.get('pp-size-finder')) customElements.define('pp-size-finder', PPSizeFinder);

// Laufband anhalten/fortsetzen (dauerhafte Pause per Tastatur und Maus, WCAG 2.2.2)
document.addEventListener('click', (event) => {
  const toggle = event.target instanceof Element ? event.target.closest('[data-pp-marquee-toggle]') : null;
  if (!toggle) return;
  const paused = toggle.getAttribute('aria-pressed') !== 'true';
  toggle.setAttribute('aria-pressed', String(paused));
  toggle.closest('.pp-trust-bar')?.classList.toggle('is-paused', paused);
});

// Ruhige Reveals: Browser ohne CSS-Scrolltimeline (z. B. Firefox) bekommen dieselbe Bewegung per IntersectionObserver
if (!CSS.supports('animation-timeline: view()') && !reducedMotion.matches) {
  document.documentElement.classList.add('pp-no-sda');
  const revealObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('is-in');
      revealObserver.unobserve(entry.target);
    }
  }, { rootMargin: '0px 0px -12% 0px' });
  const observeReveals = () => document.querySelectorAll('.pp-home .pp-rv, .pp-home #MainContent > .shopify-section:not(:first-child) > *')
    .forEach((element) => { if (!element.classList.contains('is-in')) revealObserver.observe(element); });
  observeReveals();
  document.addEventListener('shopify:section:load', observeReveals);
}
